interface RateLimitEntry {
  count: number;
  resetTime: number;
}

class RateLimitService {
  private rateLimits: Map<string, RateLimitEntry> = new Map();
  private readonly maxRequests: number;
  private readonly windowMs: number;

  constructor(maxRequests: number = 10, windowMs: number = 60000) { // 10 requests per minute
    this.maxRequests = maxRequests;
    this.windowMs = windowMs;
    
    // Clean up expired entries every 5 minutes
    setInterval(() => this.cleanup(), 5 * 60 * 1000);
  }

  private cleanup(): void {
    const now = Date.now();
    for (const [key, entry] of this.rateLimits.entries()) {
      if (now > entry.resetTime) {
        this.rateLimits.delete(key);
      }
    }
  }

  private getKey(userId: string, action: string): string {
    return `${userId}:${action}`;
  }

  public checkRateLimit(userId: string, action: string = 'email'): {
    allowed: boolean;
    remaining: number;
    resetTime: number;
  } {
    const key = this.getKey(userId, action);
    const now = Date.now();
    const entry = this.rateLimits.get(key);

    if (!entry || now > entry.resetTime) {
      // First request or window expired
      const newEntry: RateLimitEntry = {
        count: 1,
        resetTime: now + this.windowMs
      };
      this.rateLimits.set(key, newEntry);
      
      return {
        allowed: true,
        remaining: this.maxRequests - 1,
        resetTime: newEntry.resetTime
      };
    }

    if (entry.count >= this.maxRequests) {
      // Rate limit exceeded
      return {
        allowed: false,
        remaining: 0,
        resetTime: entry.resetTime
      };
    }

    // Increment count
    entry.count++;
    this.rateLimits.set(key, entry);

    return {
      allowed: true,
      remaining: this.maxRequests - entry.count,
      resetTime: entry.resetTime
    };
  }

  public getRemainingTime(userId: string, action: string = 'email'): number {
    const key = this.getKey(userId, action);
    const entry = this.rateLimits.get(key);
    
    if (!entry) return 0;
    
    const now = Date.now();
    return Math.max(0, entry.resetTime - now);
  }

  public reset(userId: string, action: string = 'email'): void {
    const key = this.getKey(userId, action);
    this.rateLimits.delete(key);
  }
}

// Create a singleton instance
export const rateLimitService = new RateLimitService();

export default rateLimitService;